import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { AlbumCard } from "@/components/AlbumCard";
import TrackList from "@/components/TrackList";
import FollowButton from "@/components/FollowButton";
import TipJarModal from "@/components/TipJarModal";
import VerifiedBadge from "@/components/VerifiedBadge";
import PayoutsPanel from "@/components/PayoutsPanel";
import ShareButtons from "@/components/ShareButtons";
import PrintfulMerchGrid from "@/components/PrintfulMerchGrid";
import LikeButton from "@/components/LikeButton";
import { Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function ArtistProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const { user: me } = useAuth();
  useEffect(() => { api.get(`/artists/${id}`).then((r) => setData(r.data)); }, [id]);

  if (!data) return <div className="p-16 text-center font-mono text-xs uppercase text-gray-500">Loading...</div>;
  const { artist, tracks, albums } = data;
  const banner = artist.banner_path ? fileUrl(artist.banner_path) : null;
  const isMine = me && me.id === artist.id;
  const isAdmin = me?.role === "admin";

  const rename = async () => {
    const current = artist.display_name || artist.username;
    const next = window.prompt(`Change display name for @${artist.username} (this is what fans see on your profile — your @handle stays the same).`, current);
    if (!next || next === current) return;
    try {
      const url = isAdmin && !isMine
        ? `/admin/users/${artist.id}/display-name`
        : `/users/me/profile`;
      await api.patch(url, { display_name: next.trim().slice(0, 60) });
      toast.success(`Display name updated to "${next}"`);
      const r = await api.get(`/artists/${id}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };

  return (
    <div className="pb-32 -mt-16 md:-mt-20">
      {/* Banner (uploaded) or default gradient — bleeds up behind the header */}
      <div data-testid="artist-banner" className="relative border-b border-[#2A2A2A] overflow-hidden pt-16 md:pt-20">
        <div className="absolute inset-0">
          {banner ? (
            <img src={banner} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full grid-lines opacity-30" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/60 to-black/20" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 pt-24 md:pt-40 pb-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-2">Artist</div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter drop-shadow-[0_2px_20px_rgba(0,0,0,0.8)] flex items-center gap-3 flex-wrap">
            {artist.display_name || artist.username}
            {artist.verified && <VerifiedBadge size={36} />}
          </h1>
          <div className="font-mono text-sm text-gray-300 mt-2">@{artist.username}</div>
          {artist.bio && <p className="mt-4 max-w-2xl text-gray-200 leading-relaxed drop-shadow-[0_1px_8px_rgba(0,0,0,0.8)]">{artist.bio}</p>}
          <div className="mt-6 flex flex-wrap gap-3">
            <FollowButton artistId={artist.id} />
            <TipJarModal artistId={artist.id} artistName={artist.display_name || artist.username} />
            {(isMine || isAdmin) && (
              <button
                data-testid="rename-artist-btn"
                onClick={rename}
                className="h-10 px-4 border border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2"
                title="Change your display name"
              >
                <Pencil className="h-3.5 w-3.5" /> Rename
              </button>
            )}
          </div>
          <div className="mt-4">
            <ShareButtons
              kind="artist"
              subject={{ name: artist.display_name || artist.username }}
              preview={{ kind: "artist", id: artist.id }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        {albums.length > 0 && <>
          <h2 className="font-display text-2xl md:text-3xl mb-4">Albums</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6 mb-10">
            {albums.map((a) => <AlbumCard key={a.id} album={a} />)}
          </div>
        </>}
        <h2 className="font-display text-2xl md:text-3xl mb-4">Tracks</h2>
        <TrackList tracks={tracks} contextList={tracks} />

        <div className="mt-12">
          <PrintfulMerchGrid
            mode="artist"
            artistId={artist.id}
            title={`${artist.display_name || artist.username} · Merch`}
            hideIfEmpty={!isMine}
          />
        </div>
      </div>
    </div>
  );
}
